'use client'

import { useClientPageProps, type ClientPageProps, useUnwrappedSearchParams } from '@/lib/client-page-dynamic'

import { Suspense, useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { signIn, useSession } from 'next-auth/react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { LoadingImage } from '@/components/LoadingImage'
import Link from 'next/link'
import { CLOUDAXE_LOGO_URL } from '@/lib/cloudaxeLogo'
import { getDeviceFingerprint } from '@/lib/device-fingerprint'

const BASE_PATH = '/hosting-control-panel'
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1'
/** Temporarily hide Apple Sign In until provider setup is ready. */
const SHOW_APPLE_SIGN_IN = false

type LoginStep = 'credentials' | 'device-verify'

function LoginForm() {
    const router = useRouter()
    const searchParams = useUnwrappedSearchParams()
    const callbackUrl = searchParams ? searchParams.get('callbackUrl') : null
    const returnToParam = searchParams ? searchParams.get('returnTo') : null
    const [isLoading, setIsLoading] = useState(false)
    const [step, setStep] = useState<LoginStep>('credentials')
    const [tempToken, setTempToken] = useState<string | null>(null)
    const [challengeMethods, setChallengeMethods] = useState<string[]>(['email'])
    const [challengeHint, setChallengeHint] = useState<string | null>(null)
    const [verifyCode, setVerifyCode] = useState('')
    const [trustDevice, setTrustDevice] = useState(true)
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    })
    const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})
    const [socialLoginMode, setSocialLoginMode] = useState<'dev' | 'live' | null>(null)

    const { data: session, status: sessionStatus } = useSession()
    const isAdmin =
        (session?.user as { role?: string } | undefined)?.role === 'ADMIN' ||
        (session?.user as { role?: string } | undefined)?.role === 'SUPER_ADMIN'
    const isImpersonating = !!(session?.user as { impersonatorId?: string | null } | undefined)
        ?.impersonatorId
    const showSocialLogins =
        socialLoginMode === 'live' ||
        (socialLoginMode === 'dev' && sessionStatus === 'authenticated' && (isAdmin || isImpersonating))

    useEffect(() => {
        if (typeof window === 'undefined' || !searchParams) return
        if (!searchParams.has('email') && !searchParams.has('password')) return
        const clean = new URL(window.location.href)
        clean.searchParams.delete('email')
        clean.searchParams.delete('password')
        window.history.replaceState(null, '', `${clean.pathname}${clean.search}${clean.hash}`)
    }, [searchParams])

    useEffect(() => {
        let cancelled = false
        async function fetchSocialLoginMode() {
            try {
                const res = await axios.get<{ success: boolean; mode?: string }>(
                    `${API_URL}/public/social-login/mode`,
                    { timeout: 5000 }
                )
                if (!cancelled && res.data?.success) {
                    setSocialLoginMode(res.data.mode === 'live' ? 'live' : 'dev')
                }
            } catch {
                if (!cancelled) setSocialLoginMode('dev')
            }
        }
        void fetchSocialLoginMode()
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return
        const host = window.location.hostname
        if (host !== 'localhost' && host !== '127.0.0.1') return
        setFormData({ email: 'support@axchost.com', password: 'Admin123!' })
    }, [])

    const passwordChangedFlag = searchParams?.get('passwordChanged') ?? null
    useEffect(() => {
        if (passwordChangedFlag === '1') {
            toast.success('Password updated. Please sign in with your new password.')
        }
    }, [passwordChangedFlag])

    /** Allow only in-app relative dashboard paths (blocks open redirects). */
    const safeReturnTo = (raw: string | null): string | null => {
        if (!raw) return null
        const value = raw.trim()
        if (!value.startsWith('/') || value.startsWith('//')) return null
        if (value.includes('://') || value.includes('\\') || value.includes('..')) return null
        if (!value.startsWith('/dashboard')) return null
        return value
    }

    const resolveRedirectTarget = () => {
        const fromReturnTo = safeReturnTo(returnToParam)
        if (fromReturnTo) return fromReturnTo

        let target = '/dashboard'
        if (callbackUrl && typeof window !== 'undefined') {
            try {
                const url = new URL(callbackUrl)
                if (url.origin === window.location.origin && url.pathname.startsWith(BASE_PATH)) {
                    target = url.pathname.slice(BASE_PATH.length) || '/dashboard'
                    if (url.search) target += url.search
                }
            } catch {
                // ignore invalid callbackUrl
            }
        }
        return target
    }

    const finishSession = async (verifiedLoginToken: string) => {
        const result = await signIn('credentials', {
            verifiedLoginToken,
            redirect: false,
        })
        if (result?.error) {
            toast.error(result.error === 'CredentialsSignin' ? 'Session could not be started' : result.error)
            return false
        }
        if (result?.ok) {
            toast.success('Login successful!')
            router.push(resolveRedirectTarget())
            return true
        }
        return false
    }

    const validateForm = (): boolean => {
        const email = formData.email.trim()
        const password = formData.password
        const errors: { email?: string; password?: string } = {}

        if (!email) {
            errors.email = 'Email is required'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = 'Please enter a valid email address'
        }
        if (!password) {
            errors.password = 'Password is required'
        }

        setFieldErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleSubmit = async () => {
        setFieldErrors({})
        if (!validateForm()) return
        setIsLoading(true)

        try {
            const deviceFingerprint = await getDeviceFingerprint()
            const response = await axios.post(
                `${API_URL}/auth/login`,
                {
                    email: formData.email.trim(),
                    password: formData.password,
                    deviceFingerprint,
                },
                { timeout: 15000 }
            )

            if (response.data.success && response.data.verifiedLoginToken) {
                await finishSession(response.data.verifiedLoginToken)
                return
            }

            if (response.data.requiresDeviceVerification && response.data.tempToken) {
                setVerifyCode('')
                setTempToken(response.data.tempToken)
                const hint =
                    typeof response.data.challengeHint === 'string'
                        ? response.data.challengeHint
                        : null
                setChallengeHint(hint)
                if (typeof window !== 'undefined') {
                    sessionStorage.setItem(
                        'deviceVerifySession',
                        JSON.stringify({
                            tempToken: response.data.tempToken,
                            challengeHint: hint,
                            issuedAt: Date.now(),
                        })
                    )
                }
                setChallengeMethods(response.data.challengeMethods ?? ['email'])
                setStep('device-verify')
                toast.success(
                    response.data.challengeMethods?.includes('sms')
                        ? 'Verification code sent to your email and phone.'
                        : 'Verification code sent to your email.'
                )
                return
            }

            const errMsg = response.data.error || 'Invalid email or password'
            toast.error(errMsg)
        } catch (error: unknown) {
            const err = error as {
                response?: { data?: { error?: string }; status?: number }
                code?: string
                message?: string
            }
            const message =
                err.response?.data?.error ||
                (err.code === 'ECONNABORTED' ? 'Login timed out. The server may be busy — try again.' : null) ||
                err.message ||
                'An error occurred during login'
            toast.error(message)
        } finally {
            setIsLoading(false)
        }
    }

    const getDeviceVerifyBearer = (): string | null => {
        if (tempToken) return tempToken
        if (typeof window !== 'undefined') {
            try {
                const raw = sessionStorage.getItem('deviceVerifySession')
                if (raw) {
                    const parsed = JSON.parse(raw) as {
                        tempToken?: string
                        challengeHint?: string
                    }
                    if (parsed.challengeHint && !challengeHint) {
                        setChallengeHint(parsed.challengeHint)
                    }
                    if (parsed.tempToken) return parsed.tempToken
                }
            } catch {
                // ignore
            }
        }
        return null
    }

    const handleVerifyDevice = async () => {
        const bearer = getDeviceVerifyBearer()
        if (!bearer || verifyCode.trim().length < 4) {
            toast.error('Enter the verification code')
            return
        }
        setIsLoading(true)
        try {
            const deviceFingerprint = await getDeviceFingerprint()
            const response = await axios.post(
                `${API_URL}/auth/verify-device`,
                {
                    code: verifyCode.trim(),
                    trustDevice,
                    deviceFingerprint,
                },
                {
                    timeout: 15000,
                    headers: { Authorization: `Bearer ${bearer}` },
                }
            )

            if (response.data.success && response.data.verifiedLoginToken) {
                if (typeof window !== 'undefined') {
                    sessionStorage.removeItem('deviceVerifySession')
                }
                await finishSession(response.data.verifiedLoginToken)
                return
            }
            toast.error(response.data.error || 'Invalid verification code. Request a new code if this keeps failing.')
        } catch (error: unknown) {
            const err = error as { response?: { data?: { error?: string } } }
            toast.error(err.response?.data?.error || 'Verification failed')
        } finally {
            setIsLoading(false)
        }
    }

    const handleResendCode = async () => {
        const bearer = getDeviceVerifyBearer()
        if (!bearer) return
        setIsLoading(true)
        try {
            await axios.post(
                `${API_URL}/auth/resend-device-code`,
                {},
                {
                    timeout: 15000,
                    headers: { Authorization: `Bearer ${bearer}` },
                }
            )
            setVerifyCode('')
            toast.success('A new code was sent. Earlier codes no longer work.')
        } catch (error: unknown) {
            const err = error as { response?: { data?: { error?: string } } }
            toast.error(err.response?.data?.error || 'Could not resend code')
        } finally {
            setIsLoading(false)
        }
    }

    const handleOAuthSignIn = async (provider: 'google' | 'apple') => {
        try {
            const fp = await getDeviceFingerprint()
            sessionStorage.setItem('deviceFingerprint', fp)
        } catch {
            // continue without fingerprint
        }
        await signIn(provider, {
            callbackUrl: `${BASE_PATH}/auth/oauth-complete`,
        })
    }

    const handlePasskeySignIn = async () => {
        if (typeof window === 'undefined' || !window.PublicKeyCredential) {
            toast.error('Passkeys are not supported in this browser.')
            return
        }
        setIsLoading(true)
        try {
            const { startAuthentication } = await import('@simplewebauthn/browser')
            const email = formData.email.trim()
            const optRes = await axios.post(
                `${API_URL}/auth/passkeys/login/options`,
                email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? { email } : {},
                { timeout: 15000 }
            )
            const optionsJSON = optRes.data?.options
            if (!optionsJSON) {
                toast.error('Could not start passkey sign-in.')
                return
            }
            const assertion = await startAuthentication({ optionsJSON })
            const deviceFingerprint = await getDeviceFingerprint()
            const response = await axios.post(
                `${API_URL}/auth/passkeys/login/verify`,
                { response: assertion, deviceFingerprint },
                { timeout: 20000, headers: { 'Content-Type': 'application/json' } }
            )
            if (response.data?.success && response.data?.verifiedLoginToken) {
                await finishSession(response.data.verifiedLoginToken)
                return
            }
            toast.error(response.data?.error || 'Passkey sign-in failed.')
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'NotAllowedError') {
                toast.error('Passkey sign-in was cancelled.')
            } else {
                const msg =
                    axios.isAxiosError(err) && err.response?.data?.error
                        ? String(err.response.data.error)
                        : 'Passkey sign-in failed.'
                toast.error(msg)
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="flex min-h-screen bg-white">
            <div className="flex flex-1 flex-col justify-center px-8 py-12 sm:px-12 lg:w-2/3">
                <div className="mx-auto w-full max-w-md">
                    <div className="mb-6 flex flex-col items-center sm:flex-row sm:items-center sm:gap-3">
                        <img
                            src={CLOUDAXE_LOGO_URL}
                            alt="CloudAxe"
                            className="h-12 w-12 flex-shrink-0"
                        />
                        <span className="text-xl font-semibold tracking-tight text-black sm:text-2xl">
                            CloudAxe Client Panel
                        </span>
                    </div>

                    {step === 'device-verify' ? (
                        <div className="mt-8 space-y-6">
                            <p className="text-sm text-gray-600">
                                We detected a sign-in from a new device. Enter the verification code sent to your{' '}
                                {challengeMethods.includes('sms') ? 'email and phone' : 'email'}.
                                {challengeHint ? (
                                    <>
                                        {' '}
                                        Use the email whose reference is{' '}
                                        <span className="font-mono font-semibold text-gray-900">{challengeHint}</span>.
                                    </>
                                ) : null}
                            </p>
                            <p className="text-xs text-gray-500">
                                If you sign in again or tap Resend, only the newest email code works. Close other
                                browser tabs on this page.
                            </p>
                            <div>
                                <label htmlFor="verify-code" className="block text-sm font-medium text-black">
                                    Verification code
                                </label>
                                <input
                                    id="verify-code"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={10}
                                    value={verifyCode}
                                    onChange={(e) =>
                                        setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 8))
                                    }
                                    disabled={isLoading}
                                    className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={trustDevice}
                                    onChange={(e) => setTrustDevice(e.target.checked)}
                                    className="rounded border-gray-300"
                                />
                                Trust this device for 30 days
                            </label>
                            <button
                                type="button"
                                onClick={() => void handleVerifyDevice()}
                                disabled={isLoading}
                                className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-70"
                            >
                                {isLoading ? (
                                    <>
                                        <LoadingImage size={18} className="mr-2" ariaLabel="Verifying" />
                                        Verifying...
                                    </>
                                ) : (
                                    'Verify and sign in'
                                )}
                            </button>
                            <div className="flex items-center justify-between text-sm">
                                <button
                                    type="button"
                                    className="font-medium text-gray-700 hover:underline"
                                    onClick={() => {
                                        setStep('credentials')
                                        setTempToken(null)
                                        setChallengeHint(null)
                                        setVerifyCode('')
                                        if (typeof window !== 'undefined') {
                                            sessionStorage.removeItem('deviceVerifySession')
                                        }
                                    }}
                                >
                                    Back to sign in
                                </button>
                                <button
                                    type="button"
                                    className="font-medium text-sky-600 hover:underline"
                                    disabled={isLoading}
                                    onClick={() => void handleResendCode()}
                                >
                                    Resend code
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div
                            role="form"
                            aria-label="Sign in"
                            className="mt-8 space-y-6"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isLoading) {
                                    e.preventDefault()
                                    void handleSubmit()
                                }
                            }}
                        >
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-black">
                                    Your email <span className="text-red-600" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder="name@mail.com"
                                    value={formData.email}
                                    onChange={(e) => {
                                        setFormData({ ...formData, email: e.target.value })
                                        if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }))
                                    }}
                                    required
                                    disabled={isLoading}
                                    aria-invalid={!!fieldErrors.email}
                                    className={`mt-2 block w-full rounded-lg border bg-white px-4 py-3 text-black placeholder-gray-400 focus:outline-none focus:ring-1 ${fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-gray-900 focus:ring-gray-900'}`}
                                />
                                {fieldErrors.email && (
                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                        {fieldErrors.email}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-black">
                                    Password <span className="text-red-600" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={formData.password}
                                    onChange={(e) => {
                                        setFormData({ ...formData, password: e.target.value })
                                        if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: undefined }))
                                    }}
                                    required
                                    disabled={isLoading}
                                    className={`mt-2 block w-full rounded-lg border bg-white px-4 py-3 text-black focus:outline-none focus:ring-1 ${fieldErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-gray-900 focus:ring-gray-900'}`}
                                />
                                {fieldErrors.password && (
                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                        {fieldErrors.password}
                                    </p>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={() => void handleSubmit()}
                                disabled={isLoading}
                                className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-70"
                            >
                                {isLoading ? (
                                    <>
                                        <LoadingImage size={18} className="mr-2" ariaLabel="Signing in" />
                                        Signing in...
                                    </>
                                ) : (
                                    'Sign In'
                                )}
                            </button>

                            <div className="relative py-1">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center text-xs">
                                    <span className="bg-white px-2 text-gray-500">or</span>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => void handlePasskeySignIn()}
                                disabled={isLoading}
                                className="flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-70"
                            >
                                Sign in with a passkey
                            </button>
                        </div>
                    )}

                    {step === 'credentials' && (
                        <>
                            <div className="mt-6">
                                <Link
                                    href="/auth/forgot-password"
                                    className="text-sm font-medium text-gray-700 hover:underline"
                                >
                                    Forgot Password
                                </Link>
                            </div>

                            {showSocialLogins && (
                                <div className="mt-8 space-y-3">
                                    <button
                                        type="button"
                                        onClick={() => void handleOAuthSignIn('google')}
                                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-wide text-black transition hover:bg-gray-50"
                                    >
                                        Sign in with Google
                                    </button>
                                    {SHOW_APPLE_SIGN_IN ? (
                                        <button
                                            type="button"
                                            onClick={() => void handleOAuthSignIn('apple')}
                                            className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-wide text-black transition hover:bg-gray-50"
                                        >
                                            Sign in with Apple
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="hidden lg:block lg:w-1/3">
                <div className="relative flex h-full min-h-[400px] items-center justify-center overflow-hidden rounded-l-3xl bg-white">
                    <img
                        src={CLOUDAXE_LOGO_URL}
                        alt=""
                        className="animate-float-slow opacity-60"
                        style={{ width: '220px', height: '220px', objectFit: 'contain' }}
                    />
                </div>
            </div>
        </div>
    )
}

export default function LoginPage(props: ClientPageProps) {
    useClientPageProps(props)

    return (
        <Suspense
            fallback={
                <div className="flex min-h-screen items-center justify-center bg-white">
                    <LoadingImage size={40} ariaLabel="Loading login" />
                </div>
            }
        >
            <LoginForm />
        </Suspense>
    )
}
